import { argon2id } from 'hash-wasm';
/**
 * Memory for Argon2id, largest first. A phone that cannot spare 64 MiB at
 * once (low-end Android WebViews refuse the allocation) seals with less, and
 * the size goes into the blob so it always opens the same way. 16 MiB is
 * still far beyond what makes guessing a PIN cheap.
 */
export const ARGON_MEMORY_KIB = [64 * 1024, 32 * 1024, 16 * 1024];
const ARGON = { iterations: 3, parallelism: 1, hashLength: 32 };
const argon = (pin, salt, memorySize) => argon2id({ password: pin, salt, memorySize, ...ARGON, outputType: 'binary' });
function b64(bytes) {
    return btoa(String.fromCharCode(...bytes));
}
function unb64(text) {
    return Uint8Array.from(atob(text), (c) => c.charCodeAt(0));
}
/** A copy backed by a plain ArrayBuffer, which WebCrypto's types insist on. */
function plain(bytes) {
    return new Uint8Array(bytes);
}
async function keyFromPin(pin, salt, memoryKiB, stretch) {
    const raw = await stretch(pin, salt, memoryKiB);
    return crypto.subtle.importKey('raw', plain(raw), 'AES-GCM', false, ['encrypt', 'decrypt']);
}
export function isPin(pin) {
    return /^\d{6}$/.test(pin);
}
export async function seal(seed, pin, stretch = argon) {
    if (!isPin(pin))
        throw new Error('A PIN is six digits');
    const salt = crypto.getRandomValues(new Uint8Array(16));
    const iv = crypto.getRandomValues(new Uint8Array(12));
    let key = null;
    let memory = ARGON_MEMORY_KIB[0];
    let last = null;
    for (const m of ARGON_MEMORY_KIB) {
        try {
            key = await keyFromPin(pin, salt, m, stretch);
            memory = m;
            break;
        }
        catch (e) {
            last = e;
        }
    }
    if (key === null)
        throw new Error(`This phone could not spare the memory to protect the wallet (${last instanceof Error ? last.message : String(last)})`);
    const data = new Uint8Array(await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plain(seed)));
    return {
        v: 1,
        salt: b64(salt),
        iv: b64(iv),
        data: b64(data),
        ...(memory === ARGON_MEMORY_KIB[0] ? {} : { m: memory }),
    };
}
/** Throws on a wrong PIN. The caller wipes the seed when done. */
export async function unseal(sealed, pin, stretch = argon) {
    const key = await keyFromPin(pin, unb64(sealed.salt), sealed.m ?? ARGON_MEMORY_KIB[0], stretch);
    try {
        const plain = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: unb64(sealed.iv) }, key, unb64(sealed.data));
        return new Uint8Array(plain);
    }
    catch {
        throw new Error('Wrong PIN');
    }
}
//# sourceMappingURL=vault.js.map